import { useEffect, useState, type ReactNode } from "react";
import type { AgencySession } from "@nabla/api";
import { PublicEntrances, publicModulePath } from "../features/auth/public-entrances.tsx";
import { authCopy } from "../features/auth/copy.ts";
import { agencyRead, type AgencyRead } from "../client/agency.ts";
import { Icon } from "../design/Icon.tsx";
import { nabla } from "../design/icons/index.ts";
import { designCopy } from "../design/copy.ts";
import { Banner } from "../design/primitives/Banner.tsx";
import { Button } from "../design/primitives/Button.tsx";
import { Page } from "../design/primitives/Page.tsx";
import { agencyCopy as t } from "./copy.ts";
import { Home } from "./Home.tsx";
import { Invite, NotFound, Out, SignOut } from "./Doors.tsx";
import { openedAt } from "./navigation.ts";
import { Team } from "./Team.tsx";
import styles from "./agency.module.css";

export function AgencyApp() {
  if (publicModulePath(openedAt)) return <main id="conteudo"><PublicEntrances page={openedAt} /></main>;
  if (openedAt === "/sair") return <SignOut />;
  if (openedAt === "/convite") return <Invite />;
  if (openedAt.startsWith("/ir/")) return <NotFound />;
  return <Signed />;
}

function Signed() {
  const [session, setSession] = useState<AgencyRead<AgencySession> | undefined>(undefined);
  const load = () => void agencyRead.sessao().then(setSession);
  useEffect(load, []);
  if (session === undefined) return <main id="conteudo" className={styles.waiting} aria-busy="true" />;
  if (!session.ok) {
    if (session.error.kind === "api" && session.error.code === "nao-autenticado") return <Out reason="terminou" />;
    return (
      <main id="conteudo">
        <Page>
          <Failed onRetry={load} />
        </Page>
      </main>
    );
  }
  return <Frame session={session.value}>{openedAt === "/equipe" ? <Team /> : <Home session={session.value} />}</Frame>;
}

export function Failed({ onRetry }: { readonly onRetry: () => void }) {
  return <Banner tone="negativo" action={<Button onClick={onRetry}>{designCopy.tentarDeNovo}</Button>}>{t.naoCarregou}</Banner>;
}

function Frame({ session, children }: { readonly session: AgencySession; readonly children: ReactNode }) {
  const here = (path: string) => (openedAt === path ? { "aria-current": "page" as const } : {});
  return (
    <>
      <header className={styles.bar}>
        <a href="/" className={styles.brand}>
          <Icon svg={nabla} size={24} label={authCopy.nabla} />
          <span>{session.agency.name}</span>
        </a>
        <nav className={styles.places}>
          <a href="/" {...here("/")}>{t.lugares.clientes}</a>
          <a href="/equipe" {...here("/equipe")}>{t.lugares.equipe}</a>
        </nav>
        <span className={styles.me}>{session.member.name}</span>
        <PublicEntrances account />
        <a href="/sair" className={styles.out}>{t.lugares.sair}</a>
      </header>
      <main id="conteudo" className={styles.main}>
        <Page>{children}</Page>
      </main>
    </>
  );
}
